/**
 * One reader for FastAPI's `detail` envelope.
 *
 * The backend puts three different things in `detail`, and which one you get
 * depends on the failure rather than the route:
 *
 * - a **plain string** — every `HTTPException(detail="…")`, the common case;
 * - a **list of per-field errors** — a 422 from request validation, where the
 *   human-readable part is each entry's `msg`;
 * - an **object** — the approve endpoint's `{message, error, request_id}`,
 *   where the message says the approval survived and `error` says what Graph
 *   refused. They are not the same sentence, so both are kept.
 *
 * This used to be parsed twice, and the two copies did not agree: the approver
 * side had no 422 branch, so a validation failure there showed a generic "try
 * again" and threw away the only sentence that said what was wrong.
 *
 * `ApiError.body` is `unknown` on purpose — the client interceptor cannot know
 * which route threw — so every branch below is a runtime check, not a cast to
 * a shape we hope is there.
 */

import { isApiError } from "@/lib/api";

export interface ApiErrorMessage {
  /** The headline to show the user. */
  message: string;
  /** The raw underlying failure, when the backend sent one alongside it. */
  detail?: string;
}

/** Read a thrown SDK error into something a user can act on. */
export function readApiError(error: unknown, fallback: string): ApiErrorMessage {
  if (!isApiError(error)) {
    return { message: fallback };
  }
  const detail = (error.body as { detail?: unknown } | undefined)?.detail;

  if (typeof detail === "string") {
    return { message: detail };
  }

  // 422: a list of per-field errors. Show the first one's `msg` — the rest are
  // usually the same field reported against a second validator.
  if (Array.isArray(detail)) {
    const first: unknown = detail[0];
    const msg =
      typeof first === "object" && first !== null ? (first as { msg?: unknown }).msg : undefined;
    return { message: typeof msg === "string" ? msg : fallback };
  }

  if (detail !== null && typeof detail === "object") {
    const shaped = detail as { message?: unknown; error?: unknown };
    return {
      message: typeof shaped.message === "string" ? shaped.message : fallback,
      detail: typeof shaped.error === "string" ? shaped.error : undefined,
    };
  }

  return { message: fallback };
}

/** The headline only, for the callers that have nowhere to put the detail. */
export function readApiErrorMessage(error: unknown, fallback: string): string {
  return readApiError(error, fallback).message;
}
